/**
 * Vintage Alert — wraps Mantine's `<Alert>` with the design system's banner
 * shape, keyed off a semantic `variant` (tone) + `emphasis`.
 *
 * Tones: info · success · warning · error · neutral · brand.
 * Emphasis:
 *   subtle  — tinted bg + 3px left stripe + tone title (default)
 *   outline — surface bg + full tone hairline + tone title
 *   solid   — filled tone bg + contrast text (use for the loudest notices)
 *
 * Mantine variants ('light' | 'filled' | 'outline') still pass through unchanged
 * for legacy call-sites; only the semantic tones engage the design CSS via the
 * `data-variant` / `data-emphasis` attributes.
 */
import { forwardRef } from 'react';
import {
  Alert as MantineAlert,
  type AlertProps as MantineAlertProps,
} from '@mantine/core';
import styles from './Alert.module.css';

type DesignVariant = 'info' | 'success' | 'warning' | 'error' | 'neutral' | 'brand';
type AlertEmphasis = 'subtle' | 'outline' | 'solid';
type MantineVariant = NonNullable<MantineAlertProps['variant']>;

export type AlertVariant = DesignVariant | MantineVariant;

const DESIGN_VARIANTS = new Set<string>([
  'info',
  'success',
  'warning',
  'error',
  'neutral',
  'brand',
]);

export interface AlertProps extends Omit<MantineAlertProps, 'variant'> {
  /**
   * Design-system tone (`info` is the default), or Mantine's raw visual variant
   * for one-off needs.
   */
  readonly variant?: AlertVariant;
  /** Emphasis for the design tones. Default `subtle`. */
  readonly emphasis?: AlertEmphasis;
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { variant = 'info', emphasis = 'subtle', className, classNames, ...rest },
  ref,
) {
  const isDesignVariant = DESIGN_VARIANTS.has(variant);

  if (!isDesignVariant) {
    return (
      <MantineAlert
        ref={ref}
        variant={variant}
        className={className ?? ''}
        {...(classNames !== undefined ? { classNames } : {})}
        {...rest}
      />
    );
  }

  const existingRoot = (classNames as { root?: string } | undefined)?.root;
  const mergedClassNames: NonNullable<MantineAlertProps['classNames']> = {
    ...(classNames ?? {}),
    root: [styles.root, existingRoot].filter(Boolean).join(' '),
  };
  return (
    <MantineAlert
      ref={ref}
      variant="light"
      data-variant={variant}
      data-emphasis={emphasis}
      classNames={mergedClassNames}
      className={[styles.root, className].filter(Boolean).join(' ')}
      {...rest}
    />
  );
});
